import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readZellDetails } from "@/office/excel/analyse";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_DIAGRAMME } from "../makros";
import { nichtPruefbar } from "../schnittstelle";

function istNackteAdresse(text: string, ziel: string): boolean {
  const t = text.trim().toLowerCase();
  return t.length === 0 || /^(https?:\/\/|www\.)/.test(t) || t === ziel.trim().toLowerCase();
}

export const lektionBarrierefreiheit: Lesson = {
  id: "barrierefreiheit",
  title: "Barrierefreie Arbeitsmappen",
  summary: "Diagramme mit Alternativtext, Links mit sprechendem Text – und die eingebaute Prüfung.",
  durationMinutes: 10,
  learningGoals: [
    "Diagrammen und Bildern einen Alternativtext geben",
    "Hyperlinks mit sprechendem Text anlegen",
    "Die Barrierefreiheitsprüfung von Excel nutzen",
  ],
  steps: [
    {
      id: "alternativtext",
      title: "Alternativtexte für Diagramme",
      explanation: [
        "Ein Diagramm ist für einen Screenreader ein leeres Rechteck, solange es keinen " +
          "Alternativtext hat. Rechtsklick auf den Diagrammrand → „Alternativtext " +
          "bearbeiten“. Der Text nennt die Aussage, nicht die Form: „Umsatz 2025 je Quartal, " +
          "stärkstes Quartal Q4 mit 1,2 Mio. €“ statt „Säulendiagramm“.",
        "„Überprüfen“ → „Barrierefreiheit überprüfen“ listet fehlende Alternativtexte, " +
          "zusammengeführte Zellen, Standardblattnamen und schwachen Kontrast – vieles davon " +
          "kennst du aus diesem Kurs.",
      ],
      keyPoints: [
        "Rechtsklick → Alternativtext bearbeiten.",
        "Die Aussage beschreiben, nicht den Diagrammtyp.",
        "Überprüfen → Barrierefreiheit überprüfen.",
      ],
      task: {
        instruction: "Gib jedem Diagramm deiner Arbeitsmappe einen Alternativtext, der die Aussage beschreibt.",
        hint: "Rechtsklick auf den Diagrammrand → Alternativtext bearbeiten → mindestens einen vollständigen Satz.",
        macro: MAKRO_DIAGRAMME,
        check: () =>
          pruefeMitMakro(MAKRO_DIAGRAMME, (bericht) => {
            const diagramme = [...bericht.gruppe("diagramm").values()];
            if (diagramme.length === 0) {
              return fail("In der Arbeitsmappe gibt es noch kein Diagramm.", "Einfügen → Empfohlene Diagramme.");
            }
            const ohne = diagramme.filter((diagramm) => (diagramm.get("alternativtext") ?? "").trim().split(/\s+/).length < 4);
            return fromCriteria(
              [
                criterion(
                  ohne.length === 0
                    ? `Alle ${diagramme.length} Diagramme haben einen aussagekräftigen Alternativtext`
                    : `Ohne brauchbaren Alternativtext: ${ohne.map((d) => d.get("name")).join(", ")}`,
                  ohne.length === 0,
                ),
              ],
              {
                success: "Die Diagramme sind jetzt auch für Screenreader verständlich.",
                failure: "Mindestens ein Diagramm hat noch keinen brauchbaren Alternativtext.",
                hint: "Ein Alternativtext aus einem oder zwei Wörtern beschreibt keine Aussage.",
              },
            );
          }),
      },
    },
    {
      id: "links",
      title: "Hyperlinks mit sprechendem Text",
      explanation: [
        "Strg + K legt in einer Zelle einen Link an – auf eine Webseite, eine Datei, eine " +
          "E-Mail-Adresse oder eine Stelle in der Mappe („Aktuelles Dokument“). Das Feld " +
          "„Anzuzeigender Text“ bestimmt, was in der Zelle steht.",
        "Wie in Word gilt: Der Text beschreibt das Ziel. „Zur Auswertung“ als Link auf das " +
          "Blatt „Auswertung“ ist ein schneller Weg durch große Mappen; eine nackte Adresse " +
          "hilft niemandem.",
      ],
      keyPoints: ["Strg + K → Ziel und anzuzeigender Text.", "Links auf Blätter machen große Mappen navigierbar."],
      task: {
        instruction: "Lege mindestens einen Hyperlink mit sprechendem Text an, etwa auf ein anderes Blatt der Mappe.",
        hint: "Zelle markieren, Strg + K → Aktuelles Dokument → Blatt wählen → Anzuzeigender Text „Zur Auswertung“.",
        check: async () => {
          const details = await readZellDetails();
          if (details === null) {
            return nichtPruefbar("Die Hyperlinks einzelner Zellen", "1.9", "Fahre mit der Maus über den Link und prüfe das Ziel.", true);
          }
          const links = details.filter((zelle) => zelle.hyperlink !== null);
          const sprechend = links.filter((zelle) => zelle.hyperlink && !istNackteAdresse(zelle.hyperlink.text, zelle.hyperlink.ziel));
          return fromCriteria(
            [
              criterion(`Hyperlinks in der Mappe: ${links.length}`, links.length > 0),
              criterion("Mindestens ein Link hat einen sprechenden Text", sprechend.length > 0),
            ],
            {
              success: "Der Link sagt, wohin er führt.",
              failure: "Es gibt noch keinen Link mit sprechendem Text.",
            },
          );
        },
      },
    },
  ],
};
